<template>
  <app-layout title="学校集計状況">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        学校集計状況確認
      </h2>
    </template>

    <div class="py-8">
      <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
        <!-- 模試選択 -->
        <div class="mb-6 bg-white overflow-hidden shadow-xl sm:rounded-lg p-6">
          <div class="flex flex-wrap items-center gap-4">
            <label class="font-medium text-gray-700">模試選択:</label>
            <select
              v-model="selectedExaminationId"
              class="border-gray-300 rounded-md shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
              <option
                v-for="examination in examinations"
                :key="examination.id"
                :value="examination.id">
                {{ examination.title }}
              </option>
            </select>
            <button
              type="button"
              :disabled="isLoading || !selectedExaminationId"
              class="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md transition focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed"
              @click="loadSchools">
              <span v-if="isLoading">読み込み中...</span>
              <span v-else>集計状況を確認する</span>
            </button>
          </div>
        </div>

        <!-- 全体サマリー -->
        <div
          v-if="hasLoaded"
          class="mb-6 bg-white overflow-hidden shadow-xl sm:rounded-lg p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">全体サマリー</h3>
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div class="bg-blue-50 p-4 rounded-lg">
              <div class="text-2xl font-bold text-blue-600">{{ totalSchools }}</div>
              <div class="text-sm text-gray-600">総学校数</div>
            </div>
            <div class="bg-green-50 p-4 rounded-lg">
              <div class="text-2xl font-bold text-green-600">{{ completeSchools }}</div>
              <div class="text-sm text-gray-600">集計完了</div>
            </div>
            <div class="bg-yellow-50 p-4 rounded-lg">
              <div class="text-2xl font-bold text-yellow-600">{{ partialSchools }}</div>
              <div class="text-sm text-gray-600">一部完了</div>
            </div>
            <div class="bg-red-50 p-4 rounded-lg">
              <div class="text-2xl font-bold text-red-600">{{ incompleteSchools }}</div>
              <div class="text-sm text-gray-600">未完了</div>
            </div>
          </div>
        </div>

        <!-- 学校一覧 -->
        <div
          v-if="hasLoaded"
          class="bg-white overflow-hidden shadow-xl sm:rounded-lg">
          <div class="p-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">学校別集計状況</h3>

            <!-- テーブル -->
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      学校名
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      生徒数
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      購入済み
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      受験完了
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      スコア計算
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ランキング
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      学校集計
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状況
                    </th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr
                    v-for="school in schools"
                    :key="school.id">
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {{ school.name }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ school.total_students }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ school.purchased_students }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ school.completed_students }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ school.scored_students }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ school.ranked_students }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span :class="school.school_aggregation_complete ? 'text-green-600' : 'text-red-600'">
                        {{ school.school_aggregation_complete ? '完了' : '未完了' }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span
                        :class="getStatusBadgeClass(school.status)"
                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                        {{ getStatusText(school.status) }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        type="button"
                        class="inline-flex items-center px-3 py-2 mr-3 text-sm font-medium text-indigo-600 border border-indigo-500 rounded-md transition focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 hover:bg-indigo-50"
                        @click="showDetail(school.id)">
                        詳細
                      </button>
                      <button
                        :disabled="deletingSchoolId === school.id"
                        class="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md transition focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed"
                        type="button"
                        @click="confirmSchoolDeletion(school.id, school.name)">
                        CSV取込データ削除
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- 空の状態 -->
            <div
              v-if="schools.length === 0"
              class="text-center py-12">
              <div class="text-gray-500">データがありません</div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="bg-white overflow-hidden shadow-xl sm:rounded-lg">
          <div class="p-6 text-gray-600 text-sm">
            「集計状況を確認する」を押すと学校別の集計結果を読み込みます。
          </div>
        </div>
      </div>
    </div>
  </app-layout>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import Swal from 'sweetalert2'
import AppLayout from '@/Layouts/AppLayout.vue'

export default {
  components: {
    AppLayout,
  },

  props: {
    schools: {
      type: Array,
      default: () => [],
    },
    examinations: {
      type: Array,
      default: () => [],
    },
    selectedExamination: {
      type: Object,
      default: null,
    },
    examinationId: {
      type: [Number, String],
      default: 1,
    },
    hasLoaded: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const selectedExaminationId = ref(
      props.examinationId !== null && props.examinationId !== undefined
        ? Number(props.examinationId)
        : null,
    )
    const deletingSchoolId = ref(null)
    const isLoading = ref(false)
    const hasLoaded = computed(() => props.hasLoaded)

    watch(
      () => props.examinationId,
      (value) => {
        if (value === null || value === undefined) {
          selectedExaminationId.value = null
          return
        }
        selectedExaminationId.value = Number(value)
      },
    )

    // 集計サマリー
    const totalSchools = computed(() => props.schools.length)
    const completeSchools = computed(() => props.schools.filter(school => school.status === 'complete').length)
    const partialSchools = computed(() => props.schools.filter(school => school.status === 'partial').length)
    const incompleteSchools = computed(() => props.schools.filter(school => ['incomplete', 'no_data'].includes(school.status)).length)

    // 模試変更時の処理
    const loadSchools = () => {
      if (!selectedExaminationId.value) {
        return
      }

      isLoading.value = true

      Inertia.get(route('admin.school-results.index'), {
        examinationId: Number(selectedExaminationId.value),
        shouldLoad: 1,
      }, {
        preserveScroll: true,
        onFinish: () => {
          isLoading.value = false
        },
      })
    }

    // ステータスバッジのクラス
    const getStatusBadgeClass = (status) => {
      const classes = {
        complete: 'bg-green-100 text-green-800',
        partial: 'bg-yellow-100 text-yellow-800',
        incomplete: 'bg-red-100 text-red-800',
        no_data: 'bg-gray-100 text-gray-800',
      }
      return classes[status] || 'bg-gray-100 text-gray-800'
    }

    // ステータステキスト
    const getStatusText = (status) => {
      const texts = {
        complete: '完了',
        partial: '一部完了',
        incomplete: '未完了',
        no_data: 'データなし',
      }
      return texts[status] || '不明'
    }

    // 詳細画面への移動
    const showDetail = (schoolId) => {
      Inertia.visit(route('admin.school-results.show', {
        schoolId,
        examinationId: selectedExaminationId.value,
      }))
    }

    const confirmSchoolDeletion = (schoolId, schoolName) => {
      if (deletingSchoolId.value) {
        return
      }

      Swal.fire({
        icon: 'warning',
        title: 'CSV取込データ削除',
        html: `学校「${schoolName}」のマークシート取込データを削除します。<br>この操作は元に戻せません。<br>削除してよろしいですか？`,
        showCancelButton: true,
        confirmButtonText: '削除する',
        cancelButtonText: 'キャンセル',
        focusCancel: true,
      }).then((result) => {
        if (!result.isConfirmed) {
          return
        }
        deletingSchoolId.value = schoolId

        Inertia.delete(route('admin.school-results.destroy', {
          schoolId,
          examinationId: selectedExaminationId.value,
        }), {
          preserveScroll: true,
          onFinish: () => {
            deletingSchoolId.value = null
          },
        })
      })
    }

    return {
      selectedExaminationId,
      totalSchools,
      completeSchools,
      partialSchools,
      incompleteSchools,
      getStatusBadgeClass,
      getStatusText,
      showDetail,
      deletingSchoolId,
      isLoading,
      confirmSchoolDeletion,
      loadSchools,
      hasLoaded,
    }
  },
}
</script>
